'use strict';

import SearchRoutes from "~/components/SearchRoutes";
import NoItems from "~/components/search/NoItems";
import Tooltip from "~/components/Tooltip";
import SearchLayout, { BeforeFilterSlot, ControlSlot, DirectionSlot, ItemsNotFoundSlot, NoItemsSlot } from "~/components/search/SearchLayout";
import SearchListItem, { ControlsSlot, TitleSlot } from "~/components/search/SearchListItem";
import SearchItemCompareButton from "~/components/search/SearchItemCompareButton";
import SearchListItemRoute from "~/components/search/SearchListItemRoute";
import withQueryClient from "~/helpers/withQueryClient";
import useAircraftCompare from "~/hooks/useAircraftCompare";
import { createRoutesValidator } from "~/helpers/validators";
import { replace } from "~/helpers/history";
import SearchSubscribeButton from "../components/SearchSubscribeButton";
import AircraftCompareButton from "../components/AircraftCompareButton";

const IMAGE_DIMENSIONS = {
    LIST: '260x160',
    DETAIL: '550x360',
}
const MAX_AIRPORTS = 8

const defaultRoutes = [{
    from: {},
    to: {},
    date: '',
    time: '',
    seats: 0,
}]

const routesValidator = createRoutesValidator()

function EmptylegsSearchWidget() {
    const layoutRef = React.useRef(null)
    const { items: aircraftCompareItems, isItemCompareInitiator, isItemInCompare, toggleItem, openCompareModal } = useAircraftCompare()
    const [selectedRoutes, setSelectedRoutes] = React.useState([...defaultRoutes])
    const [routeErrors, setRouteErrors] = React.useState(null)
    const [showRouteErrors, setShowRouteErrors] = React.useState(false)

    const handleReloadDelayed = _.debounce(reloadData, 1000)

    const { data: airports } = ReactQuery.useQuery({
        queryKey: ['search', 'airports'],
        queryFn: ({ signal }) => requestAction.initData(true, MAX_AIRPORTS, signal)
            .then(response => response.airports),
        initialData: [],
    })

    const { data: items, isSuccess, isError, error, refetch, isFetching } = ReactQuery.useQuery({
        queryKey: ['search', 'emptylegs', 'items'],
        queryFn: ({ signal }) => searchAction.fetchEmptylegItems({ 
            routes: getRoutesFormData(),
            imageDimensions: [IMAGE_DIMENSIONS.LIST, IMAGE_DIMENSIONS.DETAIL],
        }, signal)
            .then(response => response.items.reduce((result, item) => {
                    // Если одинаковые equality_key, то берем тот, у кого цена меньше

                    const index = result.findIndex((e) => e.equality_key === item.equality_key);

                    // Если нет equality_key или еще нет элемента в массиве
                    if (!item.equality_key || index === -1) {
                        result.push(item);
                        return result;
                    }

                    // Если цена нового элемента меньше - заменяем
                    if (result[index].prices.price_sort > item.prices.price_sort) {
                        result.splice(index, 1, item);
                        return result;
                    }

                    return result;
                }, [])),
        staleTime: 1000 * 60 * 15,
    })
    
    React.useEffect(() => {
        routesValidator.validate(selectedRoutes)
        setRouteErrors(routesValidator.errors)

        if (isSuccess && items.length > 0 && window.flight_id) {
            const item = items.find((item) => item.id == window.flight_id)
            item && handleItemClick(item)
        }
        
    }, [isSuccess, items])

    React.useEffect(() => {
        handleReloadDelayed()
    }, [selectedRoutes])

    function reloadData() {
        layoutRef.current.clearFilters();
        layoutRef.current.scrollTop(600);
        refetch()
    }
    
    function getRoutesFormData() {
        return JSON.stringify(defaultRoutes) === JSON.stringify(selectedRoutes)
            ? []
            : selectedRoutes
    }

    function handleChangeRoutes(routes) {
        if (JSON.stringify(selectedRoutes) === JSON.stringify(routes))
            return

        routesValidator.validate(routes)
        setShowRouteErrors(false)
        setSelectedRoutes(routes)
    }

    function getSearchLink() {
        let link = '/search';
        const query = apiAction.queryString(
            {r: searchAction.transformRoutesCompact(getRoutesFormData())}
        );
        return link + (query ? `?${query}` : '');
    }

    function onRoutesFocus() {
        layoutRef.current.scrollTop(600)
    }
    
    const handleItemClick = React.useCallback((item) => {
        $.fancybox.open({
            src: '#reactFlightSearchModal',
            afterShow: () => {
                raiseEvent('react.selectFlightOffer', {
                    item, 
                    showForm: true, 
                    showTerms: true,
                    showNotes: false,
                    buttons: ['submit'],
                    routes: selectedRoutes, 
                    imageDimensions: IMAGE_DIMENSIONS.DETAIL
                });
                replace(`emptylegs/${item.id}`)
            },
            afterClose: () => {
                raiseEvent('react.selectFlightOffer', {item: null});
                replace(`emptylegs`)
            }
        })
    }, [selectedRoutes])
    
    function handleNoItemsSetErrors() {
        setShowRouteErrors(routesValidator.hasErrors)
        layoutRef.current.scrollTop(300)
    } 

    const isRouteFull = !routesValidator.hasErrors

    const renderItem = React.useCallback(item => (
        <SearchListItem key={item.id}
            item={item}
            imageSize={IMAGE_DIMENSIONS.LIST}
            onClick={() => handleItemClick(item)}
        >
            <TitleSlot>
                <SearchItemCompareButton active={isItemInCompare(item.aircraft.id)}
                    disabled={!isItemCompareInitiator(item.aircraft.id, item.id)}
                    onClick={() => toggleItem(item.aircraft, item.id)}
                />
            </TitleSlot>

            <ControlsSlot>
                {item.routes.map(route => <SearchListItemRoute key={`routes${item.id}${route.from.city}${route.to.city}${route.date}${route.date_to}`} route={route} />)}
            </ControlsSlot>
        </SearchListItem>
    ), [handleItemClick])

    return <React.Fragment>

        <div className="el-page-tile">
            <h3>Empty Legs</h3>
            <div>
                <div style={{ fontSize: '21px', lineHeight: 1.8, fontWeight: 500 }}>
                    {tMsg('Что такое Эмпти Легс (Empty Legs) и как это работает?')}
                </div>
                <p dangerouslySetInnerHTML={{ __html: tMsg('Empty Legs — рейсы, которые возвращаются на базу после основного маршрута, их можно забронировать по специальной цене с выгодой до 75%. Отличный шанс испытать все преимущества частной авиации — максимальный комфорт, гибкость и премиальный сервис по приятной цене. </br>Тот же борт. Тот же сервис. Более гибкое планирование.') }} />
            </div>
        </div>

        <SearchLayout
            layoutRef={layoutRef}
            items={items}
            renderItem={renderItem}
            isFetching={isFetching}
            isLoaded={isSuccess}
            isError={isError}
            error={error}
            onRefetch={refetch}
        >
            <DirectionSlot>
                <SearchRoutes
                    type={'plane'}
                    routes={selectedRoutes}
                    airports={airports}
                    maxRoutes={1}
                    dropDownParent={'.search'}
                    onlyAirports={true}
                    maxAirports={MAX_AIRPORTS}
                    errors={showRouteErrors ? routeErrors : undefined}
                    onFocus={() => onRoutesFocus()}
                    onChangeRoutes={(routes) => handleChangeRoutes(routes)}
                />
            </DirectionSlot>

            <BeforeFilterSlot>
                <SearchSubscribeButton routes={selectedRoutes} />
            </BeforeFilterSlot>

            <ControlSlot>
                <AircraftCompareButton items={aircraftCompareItems} onPress={openCompareModal} />
            </ControlSlot>

            <ItemsNotFoundSlot>
                <NoItems
                    title={error || undefined}
                    link={isRouteFull ? getSearchLink() : undefined}
                    onClick={isRouteFull ? undefined : handleNoItemsSetErrors}
                />
            </ItemsNotFoundSlot>

            <NoItemsSlot>
                <NoItems
                    title={error || undefined}
                    link={isRouteFull ? getSearchLink() : undefined}
                    onClick={isRouteFull ? undefined : handleNoItemsSetErrors}
                />
            </NoItemsSlot>
        </SearchLayout>

        <Tooltip />
    </React.Fragment>
}

window.EmptylegsSearchWidget = withQueryClient(EmptylegsSearchWidget)
